import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";
import { detectLanguageFromPath } from "./utils/languageUtils";
import { initPostHog } from "./utils/posthog";

/**
 * Update Google Consent Mode based on user consent
 * This sends consent signals to GTM which then activates/deactivates tags
 */
function updateGoogleConsent(categories) {
  console.log("Updating Google Consent Mode with categories:", categories);

  // Check if gtag is available (loaded via GTM in index.html)
  if (typeof window.gtag === 'function') {
    // Update consent based on accepted categories
    window.gtag('consent', 'update', {
      'ad_storage': categories.includes('marketing') ? 'granted' : 'denied',
      'ad_user_data': categories.includes('marketing') ? 'granted' : 'denied',
      'ad_personalization': categories.includes('marketing') ? 'granted' : 'denied',
      'analytics_storage': categories.includes('analytics') ? 'granted' : 'denied',
      'functionality_storage': 'granted', // Always granted for basic functionality
      'personalization_storage': categories.includes('analytics') ? 'granted' : 'denied',
      'security_storage': 'granted' // Always granted for security
    });

    console.log("Google Consent Mode updated successfully");

    // Store consent in localStorage for reference
    if (categories.includes('analytics')) {
      localStorage.setItem("analytics_consent", "true");
      // Spin up PostHog now that analytics consent is granted (no-op if already running).
      initPostHog();
    } else {
      localStorage.removeItem("analytics_consent");
    }
  } else {
    console.warn("gtag function not available - consent mode not updated");
  }
}

/**
 * Show the cookie consent settings dialog
 * @returns {boolean} Success status
 */
export const showCookieSettings = () => {
  try {
    // Method 1: Try using the global cookieConsent object
    if (
      window.cookieConsent &&
      typeof window.cookieConsent.showSettings === "function"
    ) {
      window.cookieConsent.showSettings();
      return true;
    }

    // Method 2: Try using the global CC object (alternate name)
    if (window.CC && typeof window.CC.showSettings === "function") {
      window.CC.showSettings();
      return true;
    }

    // Method 3: Try using the direct global function we created
    if (typeof window.showCookieSettings === "function") {
      window.showCookieSettings();
      return true;
    }

    // Method 4: Find and click the settings button in the DOM
    const selectors = [
      '[data-cc="c-settings"]', // Primary selector for v3.x
      ".cc-settings-btn", // Alternate class name
      ".cc_btn_settings", // Alternate class name
      'button[aria-label="Cookie settings"]', // Semantic selector
      'button:contains("Nastavenia")', // Content-based selector
    ];

    // Try each selector until we find a matching element
    for (const selector of selectors) {
      const settingsBtn = document.querySelector(selector);
      if (settingsBtn) {
        settingsBtn.click();
        console.log(`Found settings button with selector: ${selector}`);
        return true;
      }
    }

    // If we get here, we couldn't find any way to open the settings
    console.warn("Could not open cookie settings - no method available");

    // Last resort: Reinitialize cookie consent
    initCookieConsent();

    return false;
  } catch (error) {
    console.error("Error showing cookie settings:", error);
    return false;
  }
};

/**
 * Initialize the cookie consent system
 * @returns {Promise<boolean>} Success status
 */
export const initCookieConsent = async () => {
  try {
    console.log("Initializing cookie consent with correct v3.x structure");

    // Detect language from URL (cookie consent supports sk/en, map pl to en)
    const detectedLang = detectLanguageFromPath(window.location.pathname);
    const defaultLanguage = detectedLang === 'sk' ? 'sk' : 'en';

    // Check if already initialized - return early if it is
    if (
      window.cookieConsent &&
      typeof window.cookieConsent.showSettings === "function"
    ) {
      console.log("Cookie consent already initialized (cookieConsent global)");
      return true;
    }

    if (window.CC && typeof window.CC.showSettings === "function") {
      console.log("Cookie consent already initialized (CC global)");
      return true;
    }

    // Initialize cookie consent with configuration
    const cc = CookieConsent.run({
      // Basic config
      cookie: {
        name: "cc_cookie",
        expiresAfterDays: 182,
      },

      // UI options
      guiOptions: {
        consentModal: {
          layout: "box inline",
          position: "bottom left",
          equalWeightButtons: false,
          flipButtons: true,
        },
        preferencesModal: {
          layout: "box inline",
          equalWeightButtons: false,
          flipButtons: true,
        },
      },

      // Event handlers
      onFirstConsent: ({ cookie }) => {
        console.log("onFirstConsent fired", cookie);

        // Track first-time consent decision in GA4
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'cookie_consent_first',
          cookie_analytics: cookie.categories.includes('analytics'),
          cookie_marketing: cookie.categories.includes('marketing'),
          consent_method: 'first_visit'
        });
      },

      onConsent: ({ cookie }) => {
        console.log("onConsent fired!", cookie);

        // Update Google Consent Mode with accepted categories
        if (cookie.categories) {
          updateGoogleConsent(cookie.categories);

          // Track consent update in GA4
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'cookie_consent_update',
            cookie_analytics: cookie.categories.includes('analytics'),
            cookie_marketing: cookie.categories.includes('marketing'),
            consent_method: 'user_action'
          });
        }
      },

      onModalReady: ({ modalName }) => {
        console.log("Modal ready:", modalName);
      },

      onModalShow: ({ modalName }) => {
        console.log("Modal visible:", modalName);
      },

      // Categories
      categories: {
        necessary: {
          enabled: true, // this category is enabled by default
          readOnly: true, // this category cannot be disabled
        },
        analytics: {
          enabled: false,
          readOnly: false,
          autoClear: {
            cookies: [
              {
                name: /^_ga/, // regex: match all cookies starting with '_ga'
              },
              {
                name: "_gid", // string: exact cookie name
              },
            ],
          },
        },
        marketing: {
          enabled: false,
          readOnly: false,
        },
      },

      // Language settings
      language: {
        default: defaultLanguage,
        translations: {
          sk: {
            consentModal: {
              title: "Súkromie máme len jedno",
              description:
                "Ale smerníc Európskej únie mnoho. Aj v oblasti súkromia, preto takéto cool okienko.  <br><br> Používame cookies a podobné technológie, aby web fungoval optimálne. A úprimne - aj na cielenie reklamy. Nižšie môžete vybrať, čo povolíte. ",
              acceptAllBtn: "Prijať všetky",
              acceptNecessaryBtn: "Len nevyhnutné",
              showPreferencesBtn: "Nastavenia",
            },
            preferencesModal: {
              title: "Nastavenia cookies",
              acceptAllBtn: "Prijať všetko",
              acceptNecessaryBtn: "Odmietnuť všetko",
              savePreferencesBtn: "Uložiť nastavenia",
              closeIconLabel: "Zavrieť",
              sections: [
                {
                  title: "Ako používame cookies",
                  description:
                    "Používame cookies, aby naša webstránka fungovala správne a aby sme vám poskytli čo najlepší zážitok. Nižšie si môžete prispôsobiť svoje preferencie.",
                },
                {
                  title: "Nevyhnutné cookies",
                  description:
                    "Tieto cookies sú nevyhnutné pre správne fungovanie našej webstránky. Bez nich by stránka vôbec nefungovala.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Výkonnostné a analytické",
                  description:
                    "Tieto cookies nám pomáhajú pochopiť, ako návštevníci používajú našu webstránku. Zozbierané informácie sú anonymné a pomáhajú nám zlepšovať našu stránku.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Marketingové a reklamné",
                  description:
                    "Tieto cookies nám pomáhajú zobrazovať vám relevantné reklamy na internete na základe vašich záujmov a online aktivity.",
                  linkedCategory: "marketing",
                },
              ],
            },
          },
          en: {
            consentModal: {
              title: "We only have one privacy",
              description:
                "But many European Union directives. Including in the area of privacy, hence this cool window. <br><br> We use cookies and similar technologies to make the website work optimally. And honestly - also for advertising targeting. Below you can choose what you allow.",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Only necessary",
              showPreferencesBtn: "Settings",
            },
            preferencesModal: {
              title: "Cookie settings",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Save settings",
              closeIconLabel: "Close",
              sections: [
                {
                  title: "How we use cookies",
                  description:
                    "We use cookies so that our website works properly and so that we can provide you with the best experience. Below you can customize your preferences.",
                },
                {
                  title: "Necessary cookies",
                  description:
                    "These cookies are necessary for the proper functioning of our website. Without them, the site would not work at all.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Performance and analytics",
                  description:
                    "These cookies help us understand how visitors use our website. The collected information is anonymous and helps us improve our site.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Marketing and advertising",
                  description:
                    "These cookies help us show you relevant ads on the internet based on your interests and online activity.",
                  linkedCategory: "marketing",
                },
              ],
            },
          },
        },
      },
    });

    // Verify we have a valid cookie consent instance before exposing globally
    if (!cc) {
      console.error(
        "Cookie consent initialization failed - no instance returned"
      );
      return false;
    }

    // Expose the cookie consent API globally in multiple ways for maximum compatibility
    // 1. Standard cookie consent object
    window.cookieConsent = cc;

    // 2. Alternate name (CC) for broader compatibility
    window.CC = cc;

    // 3. Direct showSettings method as a standalone function
    window.showCookieSettings = function () {
      if (cc && typeof cc.showSettings === "function") {
        cc.showSettings();
      }
    };

    // 4. Alternative name with more robust implementation
    window.openCookieSettings = function () {
      try {
        // Try all possible methods
        if (cc && typeof cc.showSettings === "function") {
          cc.showSettings();
          return;
        }

        showCookieSettings(); // Call our utility function as fallback
      } catch (error) {
        console.error("Error in openCookieSettings:", error);
      }
    };

    // Check if user has already given consent and update accordingly
    const currentCookie = CookieConsent.getCookie();
    if (currentCookie && currentCookie.categories) {
      console.log("Found existing consent, updating Google Consent Mode");
      updateGoogleConsent(currentCookie.categories);
    }

    console.log("Cookie consent successfully initialized and exposed globally");
    return true;
  } catch (error) {
    console.error("Error initializing cookie consent:", error);
    return false;
  }
};

// Export a utility function for direct use in components
export const openSettings = () => showCookieSettings();

/**
 * Update cookie consent language based on URL
 */
export const updateCookieConsentLanguage = () => {
  try {
    const detectedLang = detectLanguageFromPath(window.location.pathname);
    const newLanguage = detectedLang === 'sk' ? 'sk' : 'en';

    // Get the cookie consent instance
    const cc = window.cookieConsent || window.CC;

    if (cc && typeof cc.setLanguage === 'function') {
      cc.setLanguage(newLanguage);
      console.log(`Cookie consent language updated to: ${newLanguage}`);
      return true;
    }

    console.warn('Could not update cookie consent language - instance not found');
    return false;
  } catch (error) {
    console.error('Error updating cookie consent language:', error);
    return false;
  }
};
